import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Page, Pageable } from '../../../model/rest/base';
import {
  CorpDetails,
  NoteContent,
  SchoolStatus,
  SchoolStudentStats,
  SchoolStudentsEstimate,
  SchoolListView,
  SchoolView,
  V3StudentsFeeRequest,
  V3Note,
  V3School,
  V3SchoolDetails,
  V3StudentsFeePaymentConfirmationDetails,
} from '../../../model/v3/schools';
import {
  V3Address,
  V3ContactEntry,
  V3ContactEntryDetails,
} from '../../../model/v3/people';
import {
  appendCollection,
  appendIfDefined,
  createPageableParams,
  serializeDateInput,
} from './rest-v3-utils';

@Injectable({
  providedIn: 'root',
})
export class SchoolV3RestService {
  constructor(private http: HttpClient) {}

  private baseUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools${path}`;
  }

  private schoolUrl(schoolId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}${path}`;
  }

  listSchools(
    options: {
      name?: string | null;
      ids?: number[] | null;
      oldSchoolId?: string | null;
      country?: string | null;
      status?: SchoolStatus | null;
      expDateBefore?: Date | string | null;
      expDateAfter?: Date | string | null;
      managerId?: number | null;
      view?: string | null;
      pageable?: Pageable;
    } = {},
  ): Observable<Page<SchoolView>> {
    const {
      name,
      ids,
      oldSchoolId,
      country,
      status,
      expDateBefore,
      expDateAfter,
      managerId,
      view,
      pageable,
    } = options;
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, 'name', name);
    params = appendCollection(params, 'ids', ids ?? undefined);
    params = appendIfDefined(params, 'oldSchoolId', oldSchoolId);
    params = appendIfDefined(params, 'country', country);
    params = appendIfDefined(params, 'status', status);
    params = appendIfDefined(params, 'expDateBefore', serializeDateInput(expDateBefore));
    params = appendIfDefined(params, 'expDateAfter', serializeDateInput(expDateAfter));
    params = appendIfDefined(params, 'managerId', managerId);
    params = appendIfDefined(params, 'view', view);
    return this.http.get<Page<SchoolView>>(this.baseUrl(), { params });
  }

  listSchoolsListView(
    options: {
      name?: string | null;
      ids?: number[] | null;
      oldSchoolId?: string | null;
      country?: string | null;
      status?: SchoolStatus | null;
      expDateBefore?: Date | string | null;
      expDateAfter?: Date | string | null;
      managerId?: number | null;
      pageable?: Pageable;
    } = {},
  ): Observable<Page<SchoolListView>> {
    const {
      name,
      ids,
      oldSchoolId,
      country,
      status,
      expDateBefore,
      expDateAfter,
      managerId,
      pageable,
    } = options;
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, 'name', name);
    params = appendCollection(params, 'ids', ids ?? undefined);
    params = appendIfDefined(params, 'oldSchoolId', oldSchoolId);
    params = appendIfDefined(params, 'country', country);
    params = appendIfDefined(params, 'status', status);
    params = appendIfDefined(params, 'expDateBefore', serializeDateInput(expDateBefore));
    params = appendIfDefined(params, 'expDateAfter', serializeDateInput(expDateAfter));
    params = appendIfDefined(params, 'managerId', managerId);
    params = appendIfDefined(params, 'view', 'list');
    return this.http.get<Page<SchoolListView>>(this.baseUrl(), { params });
  }

  getSchool(schoolId: number): Observable<V3School> {
    return this.http.get<V3School>(this.schoolUrl(schoolId));
  }

  updateSchoolDetails(schoolId: number, details: V3SchoolDetails): Observable<void> {
    return this.http.put<void>(this.schoolUrl(schoolId), details);
  }


  getSchoolAddress(schoolId: number): Observable<V3Address> {
    return this.http.get<V3Address>(this.schoolUrl(schoolId, '/address'));
  }

  updateSchoolAddress(schoolId: number, address: V3Address): Observable<void> {
    return this.http.put<void>(this.schoolUrl(schoolId, '/address'), address);
  }

  listSchoolContacts(schoolId: number): Observable<V3ContactEntry[]> {
    return this.http.get<V3ContactEntry[]>(this.schoolUrl(schoolId, '/contacts'));
  }

  addSchoolContact(schoolId: number, contact: V3ContactEntryDetails): Observable<V3ContactEntry> {
    return this.http.post<V3ContactEntry>(this.schoolUrl(schoolId, '/contacts'), contact);
  }


  updateSchoolContact(
    schoolId: number,
    contactId: number,
    contact: V3ContactEntryDetails,
  ): Observable<void> {
    return this.http.put<void>(this.schoolUrl(schoolId, `/contacts/${contactId}`), contact);
  }

  deleteSchoolContact(schoolId: number, contactId: number): Observable<void> {
    return this.http.delete<void>(this.schoolUrl(schoolId, `/contacts/${contactId}`));
  }

  getSchoolCorpDetails(schoolId: number): Observable<CorpDetails> {
    return this.http.get<CorpDetails>(this.schoolUrl(schoolId, '/corp-details'));
  }

  updateSchoolCorpDetails(schoolId: number, data: CorpDetails): Observable<void> {
    return this.http.put<void>(this.schoolUrl(schoolId, '/corp-details'), data);
  }

  updateSchoolOldId(schoolId: number, newValue: string): Observable<void> {
    return this.http.patch<void>(this.schoolUrl(schoolId, '/old-school-id'), newValue);
  }

  updateSchoolExpiryDate(
    schoolId: number,
    expiryDate: Date | string,
  ): Observable<void> {
    return this.http.patch<void>(
      this.schoolUrl(schoolId, '/exp-date'),
      JSON.stringify(serializeDateInput(expiryDate)),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  getSchoolLabels(schoolId: number): Observable<string[] | null> {
    return this.http.get<string[] | null>(this.schoolUrl(schoolId, '/labels'));
  }

  updateSchoolLabels(schoolId: number, labels: string[]): Observable<void> {
    return this.http.put<void>(this.schoolUrl(schoolId, '/labels'), labels);
  }

  getSchoolEstimates(schoolId: number): Observable<SchoolStudentsEstimate | null> {
    return this.http.get<SchoolStudentsEstimate | null>(
      this.schoolUrl(schoolId, '/estimates'),
    );
  }


  updateSchoolStatus(schoolId: number, status: SchoolStatus): Observable<void> {
    return this.http.patch<void>(
      this.schoolUrl(schoolId, '/status'),
      JSON.stringify(status),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    );
  }

  listNotes(
    schoolId: number,
    options: {
      after?: Date | string | null;
      before?: Date | string | null;
      noteType?: string | null;
      pageable?: Pageable;
    } = {},
  ): Observable<Page<V3Note>> {
    const { after, before, noteType, pageable } = options;
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, 'after', serializeDateInput(after));
    params = appendIfDefined(params, 'before', serializeDateInput(before));
    params = appendIfDefined(params, 'noteType', noteType);
    return this.http.get<Page<V3Note>>(this.schoolUrl(schoolId, '/notes'), { params });
  }

  createNote(
    schoolId: number,
    content: NoteContent,
    personId?: number | null,
  ): Observable<V3Note> {
    const params = appendIfDefined(new HttpParams(), 'personId', personId ?? undefined);
    return this.http.post<V3Note>(this.schoolUrl(schoolId, '/notes'), content, { params });
  }

  updateNote(
    schoolId: number,
    noteId: number,
    content: NoteContent,
  ): Observable<void> {
    return this.http.put<void>(this.schoolUrl(schoolId, `/notes/${noteId}`), content);
  }

  deleteNote(schoolId: number, noteId: number): Observable<void> {
    return this.http.delete<void>(this.schoolUrl(schoolId, `/notes/${noteId}`));
  }

  listSchoolFees(
    schoolId: number,
    pageable?: Pageable,
  ): Observable<Page<V3StudentsFeeRequest>> {
    const params = createPageableParams(pageable);
    return this.http.get<Page<V3StudentsFeeRequest>>(
      this.schoolUrl(schoolId, '/payments/fees'),
      { params },
    );
  }

  savePaymentConfirmation(
    schoolId: number,
    feeRequestId: number,
    details: V3StudentsFeePaymentConfirmationDetails,
  ): Observable<void> {
    return this.http.put<void>(
      this.schoolUrl(schoolId, `/payments/fees/${feeRequestId}/confirmation`),
      details,
    );
  }

  getSchoolStudentStats(schoolId: number): Observable<SchoolStudentStats> {
    return this.http.get<SchoolStudentStats>(this.schoolUrl(schoolId, '/student-stats'));
  }
}
